import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { Link } from 'react-router-dom';
import { FiX, FiSearch, FiRepeat, FiBriefcase, FiUser, FiCalendar, FiArrowRight } from 'react-icons/fi';
import '../styles/ManagerDashboard.css';
import '../styles/Submissions.css';

const POOL_STAGE_TABS = [
  { key: 'ALL', label: 'All Stages' },
  { key: 'CLIENT_REVIEW', label: 'Client Review' },
  { key: 'CLIENT_INTERVIEW_1', label: 'Interview 1' },
  { key: 'CLIENT_INTERVIEW_2', label: 'Interview 2' },
  { key: 'CLIENT_FINAL_ROUND', label: 'Final Round' },
  { key: 'OFFER', label: 'Offer' },
  { key: 'ONBOARDING_INITIATED', label: 'Onboarding Init' },
  { key: 'ONBOARDING_COMPLETED', label: 'Onboarding Done' },
  { key: 'HIRED', label: 'Hired' }
];

const matchesPoolStage = (candidate, filterKey) => {
  if (!filterKey || filterKey === 'ALL') return true;
  const code = (candidate.stageCode || '').toUpperCase();
  const status = (candidate.status || candidate.submissionStatus || '').toUpperCase();

  const isHired = code === 'HIRED' || code === 'PLACED' || status === 'HIRED' || status === 'PLACED';
  if (filterKey === 'HIRED') return isHired;
  if (isHired) return false;

  const isOnboardingCompleted = code === 'ONBOARDING_COMPLETED';
  if (filterKey === 'ONBOARDING_COMPLETED') return isOnboardingCompleted;
  if (isOnboardingCompleted) return false;

  const isOnboardingInitiated = code === 'ONBOARDING_INITIATED';
  if (filterKey === 'ONBOARDING_INITIATED') return isOnboardingInitiated;
  if (isOnboardingInitiated) return false;

  const isOffer = code.includes('OFFER');
  if (filterKey === 'OFFER') return isOffer;
  if (isOffer) return false;

  const isFinalRound = code === 'CLIENT_FINAL_ROUND';
  if (filterKey === 'CLIENT_FINAL_ROUND') return isFinalRound;
  if (isFinalRound) return false;

  const isInterview2 = code === 'CLIENT_INTERVIEW_2';
  if (filterKey === 'CLIENT_INTERVIEW_2') return isInterview2;
  if (isInterview2) return false;

  const isInterview1 = code === 'CLIENT_INTERVIEW_1';
  if (filterKey === 'CLIENT_INTERVIEW_1') return isInterview1;
  if (isInterview1) return false;

  if (filterKey === 'CLIENT_REVIEW') {
    return code === 'CLIENT_REVIEW' || code === 'SUBMITTED_TO_CLIENT' || (code !== '' && !isHired && !isOnboardingCompleted && !isOnboardingInitiated && !isOffer && !isFinalRound && !isInterview2 && !isInterview1);
  }

  return code === filterKey;
};

const PoolToClientModal = ({ startDate, endDate, region, client, initialStageFilter = 'ALL', onClose }) => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeStageFilter, setActiveStageFilter] = useState(initialStageFilter || 'ALL');

  useEffect(() => {
    setActiveStageFilter(initialStageFilter || 'ALL');
  }, [initialStageFilter]);

  useEffect(() => {
    const fetchPoolToClient = async () => {
      try {
        setLoading(true);
        setError('');
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BASE_URL}/api/recruitment/applications/job-report/pool-to-client`,
          {
            params: {
              startDate,
              endDate,
              region: region || 'Onsite',
              client: client ? (typeof client === 'object' ? client.name : client) : 'all'
            },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setCandidates(response.data || []);
      } catch (err) {
        console.error('Error fetching pool-to-client candidates:', err);
        setError('Failed to load pool-to-client candidates.');
      } finally {
        setLoading(false);
      }
    };

    fetchPoolToClient();
  }, [startDate, endDate, region, client]);

  // Stage tab counts
  const stageCounts = useMemo(() => {
    const counts = { ALL: candidates.length };
    POOL_STAGE_TABS.forEach(tab => {
      if (tab.key !== 'ALL') {
        counts[tab.key] = candidates.filter(c => matchesPoolStage(c, tab.key)).length;
      }
    });
    return counts;
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    let list = candidates;
    if (activeStageFilter && activeStageFilter !== 'ALL') {
      list = list.filter(c => matchesPoolStage(c, activeStageFilter));
    }
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase().trim();
    return list.filter(c =>
      (c.candidateName && c.candidateName.toLowerCase().includes(q)) ||
      (c.candidateEmail && c.candidateEmail.toLowerCase().includes(q)) ||
      (c.recruiterName && c.recruiterName.toLowerCase().includes(q)) ||
      (c.clientName && c.clientName.toLowerCase().includes(q)) ||
      (c.jobTitle && c.jobTitle.toLowerCase().includes(q)) ||
      (c.externalJobRef && c.externalJobRef.toLowerCase().includes(q)) ||
      (c.stageCode && c.stageCode.toLowerCase().includes(q))
    );
  }, [candidates, activeStageFilter, searchQuery]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateRange = () => {
    const s = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const e = new Date(endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${s} — ${e}`;
  };

  const clientDisplayName = client ? (typeof client === 'object' ? client.name : client) : 'All Clients';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-panel"
        style={{ maxWidth: '1150px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={ev => ev.stopPropagation()}
      >
        {/* Header */}
        <div className="modal-header" style={{ background: '#f0fdfa', borderBottom: '1px solid #ccfbf1' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FiRepeat color="#0d9488" size={20} />
              <h3 className="modal-title" style={{ color: '#0f766e', margin: 0 }}>
                Pool to Client Submissions ({candidates.length} Candidate{candidates.length !== 1 ? 's' : ''})
              </h3>
            </div>
            <p className="modal-subtitle" style={{ color: '#134e4a', marginTop: '4px' }}>
              Sourced prior to period &bull; Advanced to Client during <strong>{formatDateRange()}</strong> &bull; Region: <strong>{region || 'Onsite'}</strong> &bull; Client: <strong>{clientDisplayName}</strong>
            </p>
          </div>
          <button className="modal-close-btn" onClick={onClose}><FiX size={20} /></button>
        </div>

        {/* Stage Filter Tabs */}
        <div style={{
          display: 'flex',
          gap: '6px',
          overflowX: 'auto',
          padding: '10px 20px',
          background: '#ffffff',
          borderBottom: '1px solid #f1f5f9'
        }}>
          {POOL_STAGE_TABS.map(tab => {
            const count = stageCounts[tab.key] || 0;
            const isActive = activeStageFilter === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveStageFilter(tab.key)}
                style={{
                  padding: '5px 12px',
                  borderRadius: '20px',
                  border: isActive ? '1.5px solid #0d9488' : '1px solid #e2e8f0',
                  background: isActive ? '#0d9488' : '#f8fafc',
                  color: isActive ? '#ffffff' : '#475569',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                <span>{tab.label}</span>
                <span style={{
                  background: isActive ? 'rgba(255,255,255,0.25)' : '#e2e8f0',
                  color: isActive ? '#ffffff' : '#334155',
                  padding: '1px 6px',
                  borderRadius: '10px',
                  fontSize: '10.5px',
                  fontWeight: 700
                }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div style={{ padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '260px' }}>
            <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={16} />
            <input
              type="text"
              placeholder="Search candidate, recruiter, client, job..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
                outline: 'none'
              }}
            />
          </div>
          <span style={{ fontSize: '13px', color: '#64748b' }}>
            Showing <strong>{filteredCandidates.length}</strong> of <strong>{candidates.length}</strong> candidates
          </span>
        </div>

        {/* Table Body */}
        <div className="modal-body" style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
          {loading ? (
            <div className="modal-loading">
              <div className="modal-spinner" />
              <p>Loading pool to client candidates...</p>
            </div>
          ) : error ? (
            <div className="modal-error" style={{ margin: '20px' }}>{error}</div>
          ) : filteredCandidates.length === 0 ? (
            <div className="modal-empty">
              {candidates.length === 0 ? "No candidates found who moved from prior pool to client in this period." : "No candidates matching your search."}
            </div>
          ) : (
            <div className="modal-table-wrapper">
              <table className="submissions-table" style={{ fontSize: '13px' }}>
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Recruiter</th>
                    <th>Client &amp; Job Ref</th>
                    <th>Job Title</th>
                    <th>Originally Sourced</th>
                    <th>Moved to Client</th>
                    <th>Current Stage</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCandidates.map((row, idx) => (
                    <tr key={row.submissionId || `${row.id}-${idx}`}>
                      <td>
                        <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.candidateName}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{row.candidateEmail}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 500, color: '#334155' }}>{row.recruiterName}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{row.clientName}</div>
                        <div style={{ fontSize: '12px', color: '#0d9488', fontWeight: 500 }}>{row.externalJobRef}</div>
                      </td>
                      <td>
                        <Link
                          to={`/submissions/${row.id}`}
                          state={{ jobInfo: row }}
                          className="job-title-link"
                          onClick={onClose}
                        >
                          {row.jobTitle}
                        </Link>
                      </td>
                      <td>
                        <span style={{
                          background: '#ede9fe',
                          color: '#6d28d9',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 600,
                          display: 'inline-block'
                        }}>
                          📅 {formatDate(row.sourcedAt)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: '#ccfbf1',
                          color: '#0f766e',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 700,
                          display: 'inline-block'
                        }}>
                          🚀 {formatDate(row.movedToClientAt)}
                        </span>
                      </td>
                      <td>
                        <span style={{
                          background: '#eff6ff',
                          color: '#1d4ed8',
                          padding: '3px 8px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 700,
                          border: '1px solid #bfdbfe',
                          display: 'inline-block'
                        }}>
                          {row.stageCode ? row.stageCode.replace(/_/g, ' ') : 'SUBMISSION'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <span style={{ fontSize: '12.5px', color: '#64748b' }}>
            💡 <em>These candidates reconcile the pipeline count because their initial sourcing was prior to this period.</em>
          </span>
          <button className="modal-close-footer-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default PoolToClientModal;
